import "../../../App.css";
import "../../../styles/css/login.scss";
import * as React from "react";
import { Component } from "react";
import IbssFooter from '../../../Components/Layout/Footer/IbssFooter';
import { appContext } from "../../../AppContext";
import { LogOutReason } from "../../../Providers.Api/Authentication";
import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, msalConfig } from "../../../azure-authentication-config";
import {AuthenticationResult} from "@azure/msal-common";
import { OktaAuth } from "@okta/okta-auth-js";
import SvgIcon from '@mui/material/SvgIcon';
import { Box, Grid, Typography } from '@mui/material';
import { PaperProps } from '@mui/material/Paper';
import apis from "../../../Providers.Api/apis";
import { MessageContainer, FormFieldContainer, IconContainer, TextFieldContainter, LinkContainer, FormContainer, ImageContainer, LoginContainer, LoginFrame } from "./LoginComponents";
import IbssButton from "../../../Components/Buttons/Button/IbssButton";
import IbssTextField from "../../../Components/Inputs/TextField/IbssTextField";
import IbssLink from "../../../Components/Navigation/Link/IbssLink";
import { ReactComponent as EmailIcon } from './EmailEnvelope.svg';
import { ReactComponent as LockIcon } from './PasswordLock.svg';
import LoginSplash from './LoginSplash.png';
import { RouteComponentProps } from "react-router-dom";
import IbssDialog from "../../../Components/Dialogs/BaseDialog/IbssDialog";
import { AxiosResponse } from "axios";
import { IIdentityProvider } from "./DataModel";

enum LoginPanel
{
    LoginTypeSelection = 'LOGIN_TYPE_SELECTION',
    NativeLogin = 'NATIVE_LOGIN',
    ForgottenPassword = 'FORGOTTEN_PASSWORD',
    IdentityProviderLogin = 'IDENTITY_PROVIDER_LOGIN',
}

export default class Login extends Component<IProps, IState>
{
    private labels = appContext().labels;
    private alert = appContext().alert;
    private get local() { return appContext().localStorageProvider; }
    private get session() { return appContext().sessionStorageProvider; }
    private get cache() { return appContext().cache; }
    private boxRef: React.RefObject<HTMLDivElement>; 
    private apiClient = appContext().apiClient;

    private headingStyle =
    {
            fontSize: '1.875rem',
            fontFamily: 'Source Sans Pro',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '1.5em',
            margin: '0px !important',
            letterSpacing: '0.05rem !important',


    };

    private textStyle =
        {
            fontFamily: 'Source Sans Pro',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '1.5em',
            margin: '0px !important',
            letterSpacing: '0.05rem !important',
    };

    constructor(props: IProps)
    {
        super(props);

        this.session.clear();
        this.local.clear();
        this.cache.clearAll();
        this.boxRef = React.createRef();

        const searchParams = new URLSearchParams(window.location.search);
        const reason = (searchParams.get("reason") ?? "").toLowerCase();

        let message: string;
        switch (reason)
        {
            case LogOutReason[LogOutReason.SessionExpired].toLowerCase():
                message = this.labels.HubLabelLogInAgain;
                break;

            case LogOutReason[LogOutReason.AccountIncomplete].toLowerCase():
                message = this.labels.HubLabelNoPermissionToLogIn;
                break;

            case LogOutReason[LogOutReason.LoginFailed].toLowerCase():
                message = this.labels.HubLabelLoginFailedPleaseTryAgain;
                break;

            case LogOutReason[LogOutReason.VersionChanged].toLowerCase():
                message = this.labels.funcSystemUpgradedLogInAgain_L;
                break;

            default:
                message = "";
                break;
        }

        this.state =
        {
            email: "",
            password: "",
            oktaIssuer: "",
            oktaClientID: "",
            azureClientID: "",
            azureTenantID: "",
            showPasswordField: false,
            errors: { email: "", password: "" },
            message: message,
            loginPanel: LoginPanel.IdentityProviderLogin,
            identityProviders: [],
            showPrivacyPolicyDialog: false,
            enablePrivacyPolicyButtons: false,
            privacyPolicyContent: "",
            scrollEnabled: false,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        if (!this.local.getUserPolicy())
        {
            await this.loadPrivacyPolicy();
        }
        this.checkScrollBar();
    }
    
    public checkScrollBar(): void 
    {
        const boxElement = this.boxRef.current;
        if (boxElement) 
        {
          if (boxElement.scrollHeight > boxElement.clientHeight) 
          {
            this.setState({
                scrollEnabled: false
            })
          } else 
          {
             this.setState({
                scrollEnabled: true
            })
          }
        }
    }
    
    private async loadPrivacyPolicy(): Promise<void>
    {
        try
        {
            const response = await this.apiClient.privacyPolicies.getPrivacyPolicy();
            if(response?.length > 0)
            {
                this.setState({ privacyPolicyContent: response,showPrivacyPolicyDialog: true });
            }
        } catch (error)
        {
            this.setState({ showPrivacyPolicyDialog: false });
        }
    }

    private async handleAADLogin(identityProvider: IdentityProviderView): Promise<void>
    {
        // having a SecurityProviderTypeId of 1 corresponds to a AAD security provider. 

        // if client app does not have AAD security Provider, do not proceed, early return, 
        if (!identityProvider) return;

        if (!identityProvider.clientId || !identityProvider.tenantDomain) 
        {
            return;
        }

        // msal configuration
        const myMSALObj: PublicClientApplication = new PublicClientApplication(msalConfig);

        myMSALObj.getConfiguration().auth.clientId = identityProvider.clientId;
        myMSALObj.getConfiguration().auth.authority += identityProvider.tenantDomain + "/";
        await myMSALObj.initialize();

        // set loginHint to email address so after entering email in login page, the user wouldn't have to it again in msal loginPopup.
        loginRequest.loginHint = this.state.email;

        myMSALObj.loginPopup(loginRequest)
            .then((authResult: AuthenticationResult) =>
            {
                // var issuer = ""+azureClientID;
                //myMSALObj.loginRedirect(loginRequest)
                if (authResult.idToken)
                {
                    // successful login API calls always result in an ID Token, not an access token, being returned. successful login API calls always result in an ID Token, not an access token, being returned.
                    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-core/docs/scopes.md#scope-types
                    this.props.history.push('/auth');
                }
            })
            .catch(function (error)
            {
                console.log(error);
            });
    }

    private async handleOktaLogin(identityProvider: IdentityProviderView): Promise<void>
    {
        // having a SecurityProviderTypeId of 2 corresponds to a Okta security provider. 

        // if client app does not have Okta security Provider, do not proceed, early return, 
        if (!identityProvider) return;

        if (!identityProvider.clientId || !identityProvider.tenantDomain)
        {
            return;
        }

        const config =
        {
            issuer: "https://" + identityProvider.tenantDomain,
            clientId: identityProvider.clientId,
            redirectUri: window.location.origin + "/implicit/callback",
            scopes: ['openid', 'email', 'profile'],
        };

        const authClient = new OktaAuth(config);
        await authClient.signInWithRedirect();

    }

    private async handleIdentityProvider(identityProvider: IdentityProviderView): Promise<void>
    {
        const user = this.local.getUserDetails();
        this.local.setUserDetails({
            ...user,
            userType: identityProvider.identityProviderName,
            providerType: identityProvider.securityProviderTypeId,
        })

        switch (identityProvider.securityProviderTypeId)
        {
            case 1:
                await this.handleAADLogin(identityProvider);
                break;

            case 2:
                await this.handleOktaLogin(identityProvider);
                break;
            default:
                this.alert.show(this.labels.HubLabelLoginFailed, this.labels.HubLabelUnableToLoginMessage);
                break;
        }
    }

    private handleNativeEmailLogin(): void
    {
        // handle native login submission

        const { email, password } = this.state;
        const isValid = this.validateCredentials();
        const inputValidationErrorsExist: boolean = !!this.state.errors.email || !!this.state.errors.password;
        if (!isValid || inputValidationErrorsExist) return; // if there are input validation errors, don't call the api.

        const user = this.local.getUserDetails();
        this.local.setUserDetails({
            ...user,
            userType: 'Native',
            providerType: 0,
        })

        apis.loginWithCredentials.post('authenticate2', { email: email, password: password })
            .then((res: any) =>
            {
                this.local.setToken(res?.data?.token);
                this.props.history.push('/auth');

            })
            .catch((error: any) =>
            {
                this.setState(prevState => ({
                    errors: {
                        ...prevState.errors,
                        password: error.response.data.message,
                    }
                }));
            })
    }

    private validateCredentials(): boolean
    {
        const { email, password } = this.state;
        const isEmailValid = this.validateEmail(email);
        const isPasswordValid = this.validatePassword(password);
        const isValid = isEmailValid && isPasswordValid;
        return isValid;
    }

    private validateEmail(email: string): boolean
    {
        // front end validation that tests if a string is of the form expected of an email addesss.
        // sets state.errors.email if the string is empty or doesn't match the regex pattern.
        // sets isValid to false unless there are no email errors.
        let isValid = false;
        const pattern = new RegExp(
            /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
        );

        if (!email)
        {
            this.setState(prevState => ({
                errors: {
                    ...prevState.errors,
                    email: this.labels.HubLabelEnterEmailMessage,
                }
            }));
        }
        else if (!pattern.test(email))
        {
            this.setState(prevState => ({
                errors: {
                    ...prevState.errors,
                    email: this.labels.HubLabelIncorrectEmailError
                }
            }));
        }
        else
        {
            isValid = true;
            this.setState(prevState => ({
                errors: {
                    ...prevState.errors,
                    email: '',
                }
            }));
        }
        return isValid;
    }

    private validatePassword(password: string): boolean
    {
        // front end validation that tests if password is non-empty
        // sets state.errors.password if the string is empty
        // sets isValid to false unless there are no email errors.

        let isValid = false;
        if (!password)
        {
            // updated state for email errors
            this.setState(prevState => ({
                errors: {
                    ...prevState.errors,
                    password: this.labels.HubLabelEnterPasswordMessage
                }
            }));
        }
        else 
        {
            isValid = true;
            this.setState(prevState => ({
                errors: {
                    ...prevState.errors,
                    password: '',
                }
            }));
        }
        return isValid;
    }

    private requestNewPasswordClicked()
    {
        const emailIsValid = this.validateEmail(this.state.email);
        if (!emailIsValid) return;

        apis.requestNewPassword(this.state.email)
            .then((res: AxiosResponse<string>) =>
            {
                this.alert.show("", res.data);
            })
            .catch((error: any) =>
            {
                console.log(error);
            });
    }

    private emailChanged(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void
    {
        //handle syncing up what the user is typing in the email field with email state.
        const { value } = event.target;
        this.setState({ email: value }, () => this.validateEmail(this.state.email));
    }

    private passwordChanged(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void
    {
        //handle syncing up what the user is typing in the password field with password state.
        const { value } = event.target;
        this.setState({ password: value }, () => this.validatePassword(this.state.password));
    }

    private nativeLoginClicked(): void
    {
        //handle user choosing Native Email Login by clicking on Ibss Logo
        this.setState(prevState =>
        {
            if (prevState.loginPanel === LoginPanel.NativeLogin) 
            {
                // if LoginFrame is already on the email login section, clicking on Ibss logo returns user to loginType selection section.
                return {
                    ...prevState,
                    loginPanel: LoginPanel.IdentityProviderLogin
                }
            }
            else
            {
                return {
                    ...prevState,
                    loginPanel: LoginPanel.NativeLogin
                }
            }
        })
    }

    private forgottenPasswordClicked(): void
    {
        //handle user clicking on Forgotten Password Link on Native Login screen
        this.setState({ loginPanel: LoginPanel.ForgottenPassword })
    }

    private privacyPolicyDeclined(): void
    {
        window.location.href = "https://www.intelligentbuildingssoftware.com/";
    }

    private privacyPolicyAccepted(): void
    {
        this.local.setUserPolicy(true);
        this.setState({ showPrivacyPolicyDialog: false });
    }

    private privacyPolicyScrolled(e : React.UIEvent<HTMLDivElement>): void 
    {   
        const hasScrolledToBottom = e.currentTarget.scrollHeight - e.currentTarget.clientHeight <= e.currentTarget.scrollTop + 20;
        if (hasScrolledToBottom) 
        {
            this.setState ({ enablePrivacyPolicyButtons: true });
        }
    }

    private async getUserIdentityProviders(email: string, attemptNumber: number): Promise<void>
    {
        const attempt = attemptNumber ?? 1;
        try
        {
            const userIdentityProviders = await appContext().userApiClientV1.api.user.v1.users.byUsername.identityproviders2.get<IIdentityProvider[]>
            ({
                username: email,
            });
    
            const identityProviders = userIdentityProviders.map(idProvider => IdentityProviderView.fromIdentityProvider(idProvider)).sort((a, b) => a.securityProviderTypeId - b.securityProviderTypeId);
            
            this.setState({ identityProviders: identityProviders });
        }
        catch
        {
            if(attempt === 1)
            {
                window.setTimeout(()=> this.getUserIdentityProviders(email, 2), 100);
            }
            else
            {
                this.props.history.push('/out-of-service');
            }
        }
    }

    private async handleLogin(): Promise<void>
    {
        try
        {
            await this.getUserIdentityProviders(this.state.email, 1);
            
            if(this.state.identityProviders.length === 1)
            {
                await this.handleIdentityProvider(this.state.identityProviders[0]);
            }
            else if(this.state.identityProviders.length > 1)
            {
                this.setState({ loginPanel: LoginPanel.LoginTypeSelection });
            }
            else 
            {
                this.setState({errors: {...this.state.errors, email: this.labels.HubLabelIncorrectEmailError }});
            }
        }
        catch
        {
            return;
        }
    }

    public render(): JSX.Element
    {
        return (
            <>
        <Box sx={{boxSizing: 'border-box',display: 'flex',flexDirection: 'column',alignItems: 'center',justifyContent: 'space-between',minHeight: '100vh',padding: '40px 0px', backgroundColor: (theme) => theme.palette.grey[400]}}>
            <LoginFrame className={'login-template'} sx={{width: '100%',display: 'flex',flexDirection: 'row',boxShadow: 'none',background: 'transparent',padding: 0,}}>
                <Box sx={{ width: { xs: '100%', md: '45%' }, display: 'flex',flexDirection: 'column',justifyContent: 'center',alignItems: 'center',background: '#fff',py: 1,height: '100%','@media (max-width:1052px)': { width: '100%',},}}>
            <LoginContainer square elevation={0} sx={{height: '100%',width: '100%',display: 'flex',flexDirection: 'column',justifyContent: 'center',}}>
                <Box sx={{ boxSizing: 'border-box',display: "flex",flexDirection: "row",justifyContent: "space-evenly", alignItems: "center",padding: '0px 5px 0px 0px;', gap: '5px', flex: 'none',order: 0,alignSelf: 'stretch',flexGrow: 0,}}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'row', padding: '5px', gap: '10px', boxSizing: 'border-box' }}>
                                    <img src={`/images/Logos/Roamer_logo.png`} alt="Iconics Roamer logo" onClick={() => this.nativeLoginClicked()} />
                                </Box>
                                <Box sx={{ boxSizing: 'border-box', display: "flex", flexDirection: "column", alignItems: "flex-start", padding: '0px 5px', gap: '10px', }}>
                                    <Box>
                                        <Typography sx={{ ...this.headingStyle, color: (theme) => theme.palette.text.primary }}>
                                            {this.labels.HubLabelWelcomeToHub}
                                        </Typography>
                                    </Box>
                                    <Box>
                                            <Typography variant="body2" sx={{ ...this.textStyle, color: (theme) => theme.palette.text.primary }}>
                                            {`${this.labels.HubLabelYourPortal}.`}
                                        </Typography>
                                            <Typography variant="body2" sx={{ ...this.textStyle, color: (theme) => theme.palette.text.primary }}>
                                            {this.labels.HubLabelThreeProducts}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <FormContainer sx={{flex: 1,display: 'flex',flexDirection: 'column', justifyContent: 'center',}}>
                    {
                                this.state.loginPanel === LoginPanel.LoginTypeSelection && <>
                                    <MessageContainer>
                                                <Typography variant="body2" sx={{ ...this.textStyle, color: (theme) => theme.palette.text.primary }}>
                                            {`${this.labels.HubLabelContinueToLogin}:`}
                                        </Typography>
                                    </MessageContainer>
                                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: "10px 0px", gap: "10px" }}>
                                        {this.state.identityProviders.map(i =>
                                        {
                                            return <IbssButton key={(i.id).toString()} id={(i.id).toString()} variant="contained" color="primary" fullWidth onClick={() => this.handleIdentityProvider(i)}>
                                                <Typography sx={{ fontFamily: 'Source Sans Pro', fontStyle: 'normal', fontWeight: 400, fontSize: '15px', lineHeight: '26px', letterSpacing: '0.46px' }}>
                                                    {i.identityProviderName}
                                                </Typography>
                                            </IbssButton>
                                        })}
                                    </Box>
                                </>
                            }
                            {
                                this.state.loginPanel === LoginPanel.IdentityProviderLogin &&
                                <>
                                    <MessageContainer>
                                        <Typography sx={{ ...this.textStyle, color: (theme) => theme.palette.text.primary }}>
                                            {`${this.labels.HubLabelContinueToLogin}:`}
                                        </Typography>
                                    </MessageContainer>

                                    <FormFieldContainer>
                                        <IconContainer>
                                            <SvgIcon
                                                component={EmailIcon}
                                                sx={{ color: 'grey[400]' }} // ui MidTone color
                                            />
                                        </IconContainer>
                                        <TextFieldContainter>
                                            <IbssTextField
                                                id='email'
                                                name='email'
                                                autoComplete= 'email'
                                                autoFocus
                                                label={this.labels.HubLabelEmail}
                                                placeholder={this.labels.HubLabelEmail.toLowerCase()}
                                                required={true}
                                                type="email"
                                                variant="standard"
                                                fullWidth
                                                onChange={(event) => this.emailChanged(event)}
                                                value={this.state.email}
                                                error={!!this.state.errors.email}
                                                helperText={this.state.errors.email}
                                            />
                                        </TextFieldContainter>
                                    </FormFieldContainer>
                                    <IbssButton
                                        id='login'
                                        name='login'
                                        variant='contained'
                                        color='primary'
                                        fullWidth
                                        sx={{mt:2}}
                                        onClick={() => this.handleLogin()}
                                        disabled={!this.state.email.trim()}
                                    >{this.labels.HubButtonNext}
                                    </IbssButton>
                                </>
                            }
                            {
                                this.state.loginPanel === LoginPanel.NativeLogin && <>
                                    <MessageContainer>
                                        <Typography sx={{ ...this.textStyle, color: (theme) => theme.palette.text.secondary }}>
                                            {this.labels.HubLabelSignInText}
                                        </Typography>
                                    </MessageContainer>

                                    <FormFieldContainer>
                                        <IconContainer>
                                            <SvgIcon
                                                component={EmailIcon}
                                                sx={{ color: 'grey[400]' }} // ui MidTone color
                                            />
                                        </IconContainer>
                                        <TextFieldContainter>
                                            <IbssTextField
                                                id='email'
                                                name='email'
                                                autoComplete= 'email'
                                                autoFocus
                                                label={this.labels.HubLabelEmail}
                                                placeholder={this.labels.HubLabelEmail.toLowerCase()}
                                                required={true}
                                                type="email"
                                                variant="standard"
                                                fullWidth
                                                onChange={(event) => this.emailChanged(event)}
                                                value={this.state.email}
                                                error={!!this.state.errors.email}
                                                helperText={this.state.errors.email}
                                            />
                                        </TextFieldContainter>
                                    </FormFieldContainer>

                                    <FormFieldContainer>
                                        <IconContainer>
                                            <SvgIcon
                                                component={LockIcon}
                                                sx={{ color: 'grey[400]' }} // ui MidTone color
                                            />
                                        </IconContainer>
                                        <TextFieldContainter>
                                            <IbssTextField
                                                id="password"
                                                name="password"
                                                label={this.labels.HubLabelPassword}
                                                placeholder={this.labels.HubLabelPassword.toLowerCase()}
                                                required={true}
                                                variant="standard"
                                                fullWidth
                                                inputProps={{
                                                    type: "password",
                                                    autoComplete: 'current-password'
                                                }}
                                                onChange={(event) => this.passwordChanged(event)}
                                                value={this.state.password}
                                                error={!!this.state.errors.password}
                                                helperText={this.state.errors.password}
                                            />
                                        </TextFieldContainter>
                                    </FormFieldContainer>

                                    <IbssButton
                                        id='login'
                                        name='login'
                                        variant='contained'
                                        color='primary'
                                        fullWidth
                                        sx={{mt:2}}
                                        onClick={() => this.handleNativeEmailLogin()}
                                    >{this.labels.HubLabelLogin}
                                    </IbssButton>
                                    <LinkContainer>
                                        <IbssLink to="#" underline="hover" onClick={() => this.forgottenPasswordClicked()}>
                                            {this.labels.HubLabelForgottenPassword}
                                        </IbssLink>
                                    </LinkContainer>
                                </>
                            }
                            {
                                this.state.loginPanel === LoginPanel.ForgottenPassword && <>
                                    <MessageContainer>
                                        <Typography sx={{ ...this.textStyle, color: (theme) => theme.palette.text.secondary }}>
                                            {this.labels.HubLabelRequestNewPassword}
                                        </Typography>
                                    </MessageContainer>
                                    <FormFieldContainer>
                                        <IconContainer>
                                            <SvgIcon
                                                component={EmailIcon}
                                                sx={{ color: 'grey[400]' }} // ui MidTone color
                                            />
                                        </IconContainer>
                                        <TextFieldContainter>
                                            <IbssTextField
                                                id='email'
                                                name='email'
                                                autoComplete= 'email'
                                                autoFocus
                                                label={this.labels.HubLabelEmail}
                                                placeholder={this.labels.HubLabelEmail.toLowerCase()}
                                                required={true}
                                                type='email'
                                                variant="standard"
                                                fullWidth
                                                onChange={(event) => this.emailChanged(event)}
                                                value={this.state.email}
                                                error={!!this.state.errors.email}
                                                helperText={this.state.errors.email}
                                            />
                                        </TextFieldContainter>
                                    </FormFieldContainer>
                                    <Box mt={2} width={'100%'}>
                                    <IbssButton
                                        id='request-new-password'
                                        name='request-new-password'
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={() => this.requestNewPasswordClicked()}
                                    >
                                        <Typography sx={{ fontFamily: 'Source Sans Pro', fontStyle: 'normal', fontWeight: 400, fontSize: '15px', lineHeight: '26px', letterSpacing: '0.46px' }}>
                                            {this.labels.HubLabelSubmitRequest}
                                        </Typography>
                                    </IbssButton>
                                    <LinkContainer>
                                        <IbssLink to="#" underline="hover" onClick={() => this.nativeLoginClicked()}>
                                            {`< ${this.labels.HubButtonBack}`}
                                        </IbssLink>
                                    </LinkContainer>
                                    </Box>
                                </>
                            }
                            </FormContainer>
                        </LoginContainer>
                </Box>
                <Box
                    sx={{ width: '55%', display: { xs: 'none', md: 'flex' },alignItems: 'stretch',justifyContent: 'center',overflow: 'hidden',p: 0, m: 0,
                        '@media (max-width:1052px)': {
                            display: 'none',
                        },
                    }}>
                    <img
                        style={{ width: '100%',height: '100%',objectFit: 'cover',objectPosition: 'right center',display: 'block',minHeight: '100%',}}
                        src={LoginSplash}
                        alt="Meeting location in office next to the window"
                    />
                </Box>
            </LoginFrame>
            <IbssFooter />
        </Box>
        <IbssDialog
                id="privacyPolicy"
                sx={{padding:"20px"}}
                open={this.state.showPrivacyPolicyDialog}
                fullWidth
                dialogContent=
                {
                <>
                    <Box sx={{width:"100%",display:'flex',justifyContent:'center',px:0,}}>
                        <Box className="d-flex">
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'row', padding: '5px', gap: '10px', boxSizing: 'border-box' }}>
                                <img src={`/images/Logos/Roamer_logo.png`} alt="Iconics Roamer logo" />
                            </Box>
                            <Box sx={{ boxSizing: 'border-box', display: "flex", flexDirection: "column", alignItems: "flex-start", padding: '0px 5px', gap: '10px', }}>
                                <Box>
                                    <Typography sx={{ ...this.headingStyle, color: (theme) => theme.palette.text.primary }}>
                                        {this.labels.HubLabelWelcomeToHub}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ ...this.textStyle, color: (theme) => theme.palette.text.secondary }}>
                                        {this.labels.funcPolicyModal_Subtitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box className="text-center">
                        <Box tabIndex={0}  aria-label="privacy policy and license" ref={this.boxRef} className="border-theme-light rounded text-justify my-3" onScroll={e => this.privacyPolicyScrolled(e)} sx={{ height:"calc(100vh - 475px);", overflow: 'auto', padding:1,fontSize:"13px" }} dangerouslySetInnerHTML={{__html: this.state.privacyPolicyContent}}>
                        </Box>
                        <Box className="mt-3">
                            Consent below to our <a href="#policy" className="primary-link">Privacy Policy</a> and <a href="#license" className="primary-link">EULA</a>.
                        </Box>
                    </Box>
                </>
                }
                footer = 
                {
                    <>
                        <div className="d-flex justify-content-center w-100">
                            <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                <IbssButton color="primary" variant="contained"
                                        onClick={() => this.privacyPolicyAccepted()}
                                        disabled={!(this.state.enablePrivacyPolicyButtons || (this.state.scrollEnabled))}
                                    >
                                        {this.labels.funcAccept}
                                </IbssButton>
                                <IbssButton color="primary" variant="contained"
                                        onClick={() => this.privacyPolicyDeclined()}
                                        disabled={!(this.state.enablePrivacyPolicyButtons || (this.state.scrollEnabled))}
                                    >
                                        {this.labels.funcCateringOrderDecline_S}
                                </IbssButton>
                            </Box>
                        </div>
                    </>
                }
            />
        </>
        );
    }
}

interface IProps extends RouteComponentProps, PaperProps
{
}

interface IState
{
    email: string;
    password: string;
    oktaIssuer: string;
    oktaClientID: string;
    azureClientID: string;
    azureTenantID: string;
    showPasswordField: boolean;
    errors: IValidationError
    message: string;
    loginPanel: LoginPanel;
    identityProviders: IdentityProviderView[];
    showPrivacyPolicyDialog: boolean;
    enablePrivacyPolicyButtons: boolean;
    privacyPolicyContent: string;
    scrollEnabled: boolean;
}

interface IValidationError
{
    email: string;
    password: string;
}

export class IdentityProviderView
{
    public clientId = '';
    public id = 0;
    public identityProviderName = '';
    public securityProviderTypeId = 0;
    public tenantDomain = '';

    public static fromIdentityProvider(identityProvider: IIdentityProvider): IdentityProviderView
    {
        return {
            clientId: identityProvider.clientId ?? '',
            id: identityProvider.id,
            identityProviderName:  identityProvider.name ?? '',
            securityProviderTypeId: identityProvider.securityProviderTypeId,
            tenantDomain: identityProvider.tenantDomain ?? '',
        }
    }
}